import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Package, Factory, Calendar, RefreshCw, Loader2, ChevronRight, Home, Clock, CheckCircle2, XCircle, AlertCircle } from 'lucide-react'
import { Card } from '../../../components/ui/Card'
import { Badge } from '../../../components/ui/Badge'
import { useLanguage } from '../../../context/LanguageContext'
import { useFactory } from '../../../context/FactoryContext'
import { useTerminal } from '../../../context/TerminalContext'
import { findLineForTerminal, buildLineFromTerminal } from '../../../utils/terminal/terminalLine'
import { useLineOrders } from '../../../hooks/useProductionData'
import { api } from '../../../lib/api'
import ManufacturingOrderDetailPanel from './ManufacturingOrderDetailPanel'

function OrdenesFabricacion() {
  const navigate = useNavigate()
  const { t } = useLanguage()
  
  const { selectedFactory } = useFactory()
  const { isHmiMode, terminal, loading: terminalLoading } = useTerminal()
  const [selectedLine, setSelectedLine] = useState(null)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [error, setError] = useState(null)
  const [linesStatus, setLinesStatus] = useState({}) // Estado de InfluxDB por línea
  const [lastSyncDate, setLastSyncDate] = useState(() => {
    // Cargar última fecha de sincronización desde localStorage
    const saved = localStorage.getItem('lastSAPSyncDate')
    return saved ? new Date(saved) : null
  })
  const [isSyncing, setIsSyncing] = useState(false)
  
  // Manejar errores de extensiones del navegador
  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      // Obtener el mensaje de error de diferentes formas
      let errorMessage = ''
      
      if (event.reason) {
        if (typeof event.reason === 'object' && event.reason.message) {
          errorMessage = event.reason.message
        } else if (typeof event.reason === 'string') {
          errorMessage = event.reason
        } else {
          errorMessage = String(event.reason)
        }
      }
      
      // Filtrar errores comunes de extensiones del navegador
      if (
        errorMessage.includes('A listener indicated an asynchronous response') ||
        errorMessage.includes('message channel closed') ||
        errorMessage.includes('message channel closed before a response was received') ||
        errorMessage.includes('Extension context invalidated') ||
        errorMessage.includes('Receiving end does not exist') ||
        errorMessage.includes('asynchronous response')
      ) {
        // Silenciar estos errores específicos de extensiones
        event.preventDefault()
        event.stopPropagation()
        return false
      }
    }

    // Registrar el listener con capture phase para interceptar antes
    window.addEventListener('unhandledrejection', handleUnhandledRejection, true)
    
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection, true)
    }
  }, [])
  
  // Limpiar línea y orden al cambiar fábrica (excepto en HMI: la fábrica la fija el terminal)
  useEffect(() => {
    if (isHmiMode) return
    setSelectedLine(null)
    setSelectedOrder(null)
  }, [selectedFactory, isHmiMode])

  // Estados para líneas (similar a MaterialesConsumos)
  const [lines, setLines] = useState([])
  const [linesWithData, setLinesWithData] = useState([])
  const [loadingLines, setLoadingLines] = useState(true)
  const [linesError, setLinesError] = useState(null)
  const [loadingLineData, setLoadingLineData] = useState(false)
  
  
  // Helper para normalizar la fábrica de una línea (igual que MaterialesConsumos)
  const getFactoryFromLine = (line) => {
    if (line?.factory) {
      return line.factory
    }
    if (line?.hierarchical_level?.[0]) {
      return line.hierarchical_level[0]
    }
    return ''
  }

  // Función loadLines (igual que MaterialesConsumos)
  const loadLines = async () => {
    try {
      setLoadingLines(true)
      setLinesError(null)
      console.log('🔄 Cargando líneas - OrdenesFabricacion')
      console.log('📍 Factory seleccionada:', selectedFactory)
      
      // Usar /api/asset/list directamente (igual que MaterialesConsumos)
      const assets = await api.get('/api/asset/list')
      
      console.log('📦 Assets recibidos de la API:', assets?.length || 0)
      
      // Encontrar el ID de la fábrica seleccionada si existe
      let selectedFactoryId = null
      if (selectedFactory) {
        const factoryAsset = (assets || []).find(a => 
          (a.Code === selectedFactory || a.code === selectedFactory) && 
          (a.ParentID === null || a.parentID === null)
        )
        if (factoryAsset) {
          selectedFactoryId = factoryAsset.ID || factoryAsset.id
          console.log('🏭 Fábrica seleccionada encontrada:', {
            code: factoryAsset.Code || factoryAsset.code,
            id: selectedFactoryId
          })
        }
      }
      
      // Filtrar solo líneas de producción (igual que MaterialesConsumos)
      let linesData = (assets || []).filter(asset => {
        const code = asset.Code || asset.code || ''
        const parentId = asset.ParentID || asset.parentID
        const hierarchicalLevel = asset.hierarchical_level || asset.HierarchicalLevel
        
        // Verificar si es una línea por código
        const isLineByCode = code && (
          code.startsWith('Line_') || 
          code.startsWith('L') ||
          code.toLowerCase().includes('line') ||
          code.match(/^Line_\d+/i) ||
          code.match(/^L\d+/i)
        )
        
        // Verificar si es una línea por hierarchical_level (nivel 2 = fábrica + línea)
        const isLineByLevel = hierarchicalLevel && Array.isArray(hierarchicalLevel) && hierarchicalLevel.length === 2
        
        // Debe tener un ParentID (no es null, es decir, pertenece a una fábrica)
        const hasParent = parentId !== null && parentId !== undefined
        
        const isLine = isLineByCode || isLineByLevel
        
        return isLine && hasParent
      })
      
      console.log('🔍 Líneas encontradas después del filtro inicial:', linesData.length)
      
      // Filtrar por fábrica si se especifica
      if (selectedFactoryId !== null) {
        linesData = linesData.filter(line => {
          const lineParentId = line.ParentID || line.parentID
          return lineParentId === selectedFactoryId
        })
        console.log(`📊 Filtrado: ${linesData.length} líneas después del filtro por fábrica ${selectedFactory}`)
      }
      
      // Si no hay líneas con el filtro estricto, intentar un filtro más permisivo
      if (linesData.length === 0 && assets && assets.length > 0) {
        console.warn('⚠️ No se encontraron líneas con el filtro estricto, intentando filtro más permisivo...')
        
        const fallbackLines = (assets || []).filter(asset => {
          const parentId = asset.ParentID || asset.parentID
          const hierarchicalLevel = asset.hierarchical_level || asset.HierarchicalLevel
          const hasParent = parentId !== null && parentId !== undefined
          const hasTwoLevels = hierarchicalLevel && Array.isArray(hierarchicalLevel) && hierarchicalLevel.length === 2
          return hasParent && hasTwoLevels
        })
        
        if (fallbackLines.length > 0) {
          console.warn(`✅ Encontradas ${fallbackLines.length} líneas con filtro permisivo`)
          linesData = fallbackLines
        }
      }
      
      // Mapear las líneas a un formato consistente
      const mappedLines = linesData.map(line => {
        const parentId = line.ParentID || line.parentID
        const lineCode = line.Code || line.code || ''
        
        // Buscar el asset padre (fábrica) usando el ParentID
        let factoryCode = selectedFactory || ''
        if (parentId !== null && parentId !== undefined && assets) {
          const parentAsset = assets.find(a => (a.ID || a.id) === parentId)
          if (parentAsset) {
            factoryCode = parentAsset.Code || parentAsset.code || selectedFactory || ''
          }
        }
        
        if (!factoryCode) {
          factoryCode = selectedFactory || ''
        }
        
        return {
          ...line,
          code: lineCode,
          id: line.ID || line.id || `line-${lineCode}`,
          parentId: parentId,
          factory: factoryCode,
          line: lineCode,
          hierarchical_level: line.hierarchical_level || [factoryCode, lineCode]
        }
      })
      
      console.log('✅ Líneas cargadas:', mappedLines.length)
      setLines(mappedLines)
      setLinesError(null)
    } catch (err) {
      // Si es un error 401, no mostrar error crítico (ya se está redirigiendo al login)
      if (err.message?.includes('Unauthorized') || err.status === 401) {
        console.warn('🔐 Sesión expirada, se redirigirá al login')
        setLinesError(null) // No mostrar error, solo esperar redirección
        setLines([])
      } else {
        console.error('❌ Error loading lines:', err)
        setLinesError(err)
        setLines([])
      }
    } finally {
      setLoadingLines(false)
    }
  }

  // Función loadLinesWithData (igual que MaterialesConsumos)
  const loadLinesWithData = async () => {
    if (lines.length === 0) {
      console.log('⚠️ No hay líneas para cargar datos')
      setLinesWithData([])
      setLoadingLineData(false)
      return
    }
    
    try {
      setLoadingLineData(true)
      const linesToLoad = lines.slice(0, 10) // Limitar a las primeras 10
      console.log('🔄 Cargando datos completos para', linesToLoad.length, 'líneas')
      
      const linesData = await Promise.all(
        linesToLoad.map(async (line) => {
          try {
            let factory = getFactoryFromLine(line)
            if (!factory && selectedFactory) {
              factory = selectedFactory
            }
            
            const prodLine = line.line || line.code || ''
            const sapCode = line.sap_code || line.code || line.line || ''
            
            if (!factory || !prodLine) {
              return {
                ...line,
                orders: [],
                activeOrder: null,
                recipe: null
              }
            }
            
            // Cargar órdenes para esta línea
            let ordersResponse
            try {
              ordersResponse = await api.post('/api/sap/orders', {}, {
                headers: {
                  'Content-Type': 'application/json',
                  'Factory': factory,
                  'ProdLine': prodLine,
                  'SapCode': sapCode,
                  'OrderFilter': 'pending',
                }
              })
            } catch (corsError) {
              try {
                ordersResponse = await api.post('/api/sap/orders', {}, {
                  headers: {
                    'Content-Type': 'application/json',
                    'Factory': factory,
                    'ProdLine': prodLine,
                    'SapCode': sapCode,
                    'SapRequest': 'false',
                    'OrderFilter': 'pending',
                  }
                })
              } catch (retryError) {
                throw corsError
              }
            }
            
            const ordersData = Array.isArray(ordersResponse) ? ordersResponse : []
            console.log(`✅ Órdenes cargadas para ${prodLine}:`, ordersData.length)
            
            // Buscar orden activa: primero buscar orden en producción (StartedAt sin FinishedAt)
            let activeOrder = ordersData.find(order => 
              (order.StartedAt || order.StarteddAt) && !order.FinishedAt
            )
            
            // Si no hay orden activa, buscar la primera orden que no haya completado su fecha de fin
            if (!activeOrder) {
              const now = new Date()
              activeOrder = ordersData.find(order => {
                const finishedAt = order.FinishedAt ? new Date(order.FinishedAt) : null
                // Orden que aún no ha completado su fecha de fin programada
                return !finishedAt || now <= finishedAt
              })
            }
            
            // Si aún no hay orden activa, tomar la primera disponible (puede ser una orden completada)
            if (!activeOrder && ordersData.length > 0) {
              activeOrder = ordersData[0]
            }
            
            return {
              ...line,
              orders: ordersData,
              activeOrder: activeOrder,
              recipe: null
            }
          } catch (err) {
            console.error(`❌ Error cargando datos para línea:`, err)
            return {
              ...line,
              orders: [],
              activeOrder: null,
              recipe: null,
              error: err.message
            }
          }
        })
      )
      
      console.log('✅ Datos completos cargados:', linesData.length, 'líneas')
      const validLines = linesData.filter(line => line.id || line.code)
      setLinesWithData(validLines)
    } catch (err) {
      // Si es un error 401, no mostrar error crítico (ya se está redirigiendo al login)
      if (err.message?.includes('Unauthorized') || err.status === 401) {
        console.warn('🔐 Sesión expirada al cargar datos de líneas')
      } else {
        console.error('Error loading lines with data:', err)
      }
    } finally {
      setLoadingLineData(false)
    }
  }

  // Cargar líneas cuando cambia la fábrica
  useEffect(() => {
    loadLines()
  }, [selectedFactory])

  // Cargar datos de líneas cuando se cargan las líneas básicas
  useEffect(() => {
    if (lines.length > 0) {
      loadLinesWithData()
    }
  }, [lines])

  // En modo HMI, fijar la línea según el terminal (IP → línea en backend)
  useEffect(() => {
    if (!isHmiMode || !terminal?.bound || selectedLine) return
    if (loadingLines || terminalLoading) return

    const matchedLine = findLineForTerminal(lines, terminal)
    if (matchedLine) {
      setSelectedLine(matchedLine)
      setSelectedOrder(null)
      return
    }

    const fallbackLine = buildLineFromTerminal(terminal)
    if (fallbackLine) {
      setSelectedLine({
        ...fallbackLine,
        hierarchical_level: [terminal.factory, terminal.prodLine],
      })
      setSelectedOrder(null)
    }
  }, [isHmiMode, terminal, lines, selectedLine, loadingLines, terminalLoading])

  // Debug: Log lines cuando cambian
  useEffect(() => {
    console.log('🔍 Estado de líneas:', {
      linesLength: lines.length,
      linesWithDataLength: linesWithData.length,
      loadingLines,
      loadingLineData,
      linesError: linesError?.message,
      selectedFactory
    })
  }, [lines, linesWithData, loadingLines, loadingLineData, linesError, selectedFactory])
  
  // Usar linesWithData directamente (ya viene con órdenes cargadas)
  const linesWithOrders = linesWithData.map(lineData => ({
    line: lineData,
    orders: lineData.orders || [],
    activeOrder: lineData.activeOrder || null,
    isLoading: loadingLineData,
    error: lineData.error
  }))
  
  // Hook para órdenes pendientes de la línea seleccionada (consumos no enviados a SAP)
  const { data: orders = [], isLoading: loadingOrders, error: ordersError, refetch: refetchOrders } = useLineOrders(selectedLine, false, 'pending')

  // Hook para historial: órdenes con todos los períodos enviados a SAP
  const {
    data: sentToSAPOrders = [],
    isLoading: loadingSentOrders,
    error: sentOrdersError,
    refetch: refetchSentOrders,
  } = useLineOrders(selectedLine, false, 'sent_to_sap')
  
  // Hook adicional para sincronización desde SAP
  const { refetch: refetchFromSAP } = useLineOrders(selectedLine, true, 'pending')
  
  // Estado para almacenar las órdenes que realmente vienen de SAP (para comparación Prioridad 3)
  const [ordersFromSAP, setOrdersFromSAP] = useState([])
  
  // Cuando cambia la línea seleccionada, limpiar las órdenes de SAP y cargar nuevas
  useEffect(() => {
    setOrdersFromSAP([])
    
    // Cargar órdenes desde SAP cuando se selecciona una línea (para comparación Prioridad 3)
    if (selectedLine) {
      // Cargar órdenes desde SAP en segundo plano para comparación
      refetchFromSAP().then(result => {
        const sapOrders = Array.isArray(result?.data) ? result.data : []
        
        // Normalizar OrderNumbers (trim espacios en blanco) para comparación
        const normalizedSapOrders = sapOrders.map(o => ({
          ...o,
          OrderNumber: (o.OrderNumber || o.AUFNR || '').trim()
        }))
        
        setOrdersFromSAP(normalizedSapOrders)
        console.log('📋 Órdenes desde SAP cargadas automáticamente (Prioridad 3):', {
          count: normalizedSapOrders.length,
          orderNumbers: normalizedSapOrders.map(o => o.OrderNumber),
          orders: normalizedSapOrders
        })
        
        // Log específico para debugging de Prioridad 3 - orden 000010112079
        const order112079 = normalizedSapOrders.find(o => o.OrderNumber === '000010112079')
        if (!order112079) {
          console.log('🔴 PRIORIDAD 3: Orden 000010112079 NO está en la lista de SAP - debería marcarse como completada')
        } else {
          console.log('⚠️ Orden 000010112079 SÍ está en la lista de SAP (verificar con usuario si esto es correcto)')
        }
      }).catch(err => {
        console.warn('⚠️ No se pudieron cargar órdenes desde SAP para comparación:', err)
      })
    }
  }, [selectedLine?.code]) // Solo cuando cambia la línea
  
  // Debug: Log cuando se selecciona una línea o cambian las órdenes
  useEffect(() => {
    if (selectedLine) {
      console.log('🔍 Línea seleccionada:', {
        code: selectedLine.code,
        factory: selectedLine.factory,
        sap_code: selectedLine.sap_code,
        id: selectedLine.id
      })
      console.log('🔍 Estado de órdenes:', {
        ordersCount: orders.length,
        ordersFromSAPCount: ordersFromSAP.length,
        loadingOrders,
        ordersError: ordersError?.message,
        orders: orders.slice(0, 2),
        ordersFromSAP: ordersFromSAP.slice(0, 2)
      })
    }
  }, [selectedLine, orders, ordersFromSAP, loadingOrders, ordersError])
  
  const handleRefreshSAP = async () => {
    if (selectedLine) {
      // Sincronizar desde SAP cuando se hace clic en "Actualizar desde SAP"
      console.log('🔄 Sincronizando órdenes desde SAP para línea:', selectedLine.code)
      setIsSyncing(true)
      try {
        // Llamar a refetchFromSAP que envía SapRequest: 'true' al backend
        const result = await refetchFromSAP()
        
        // Guardar las órdenes que realmente vienen de SAP para comparación (Prioridad 3)
        // React Query refetch devuelve { data, ... } donde data es el resultado de la queryFn
        const sapOrders = Array.isArray(result?.data) ? result.data : []
        // Normalizar OrderNumbers (trim espacios en blanco) para comparación
        const normalizedSapOrders = sapOrders.map(o => ({
          ...o,
          OrderNumber: (o.OrderNumber || o.AUFNR || '').trim()
        }))
        
        console.log('📋 Órdenes desde SAP (Prioridad 3):', {
          count: normalizedSapOrders.length,
          orderNumbers: normalizedSapOrders.map(o => o.OrderNumber),
          allOrders: normalizedSapOrders,
          resultStructure: Object.keys(result || {})
        })
        
        // Log detallado para debugging
        if (normalizedSapOrders.length > 0) {
          console.log('✅ Primera orden desde SAP:', {
            OrderNumber: normalizedSapOrders[0].OrderNumber,
            StartedAt: normalizedSapOrders[0].StartedAt,
            FinishedAt: normalizedSapOrders[0].FinishedAt,
            QuantityRemainedToProduce: normalizedSapOrders[0].QuantityRemainedToProduce
          })
          
          // Verificar específicamente la orden 000010112079
          const order112079 = normalizedSapOrders.find(o => o.OrderNumber === '000010112079')
          if (!order112079) {
            console.log('🔴 PRIORIDAD 3: Orden 000010112079 NO está en la lista de SAP - debería marcarse como completada')
          } else {
            console.log('⚠️ Orden 000010112079 SÍ está en la lista de SAP (verificar con usuario si esto es correcto)')
          }
        } else {
          console.warn('⚠️ No se recibieron órdenes desde SAP')
        }
        
        // Guardar órdenes normalizadas
        setOrdersFromSAP(normalizedSapOrders)
        
        // También invalidar la query normal para que se recargue
        await refetchOrders()
        await refetchSentOrders()
        
        // Actualizar última fecha de sincronización solo si fue exitosa
        const syncDate = new Date()
        setLastSyncDate(syncDate)
        localStorage.setItem('lastSAPSyncDate', syncDate.toISOString())
        
        console.log('✅ Sincronización desde SAP completada exitosamente', {
          ordersCount: sapOrders.length,
          orderNumbers: sapOrders.map(o => o.OrderNumber),
          syncDate: syncDate.toISOString()
        })
      } catch (error) {
        console.error('❌ Error al sincronizar desde SAP:', error)
        // Si falla la sincronización desde SAP, intentar cargar desde caché
        try {
          await refetchOrders()
        } catch (refetchError) {
          console.error('❌ Error al recargar órdenes desde caché:', refetchError)
        }
      } finally {
        setIsSyncing(false)
      }
    } else {
      // Recargar líneas y sus datos
      setIsSyncing(true)
      try {
        await loadLines()
        if (lines.length > 0) {
          await loadLinesWithData()
        }
        
        // Actualizar última fecha de sincronización
        const syncDate = new Date()
        setLastSyncDate(syncDate)
        localStorage.setItem('lastSAPSyncDate', syncDate.toISOString())
        
        console.log('✅ Datos de líneas recargados')
      } catch (error) {
        console.error('❌ Error al recargar líneas:', error)
      } finally {
        setIsSyncing(false)
      }
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      // Verificar si la fecha es válida (no es una fecha zero o inválida)
      if (isNaN(date.getTime()) || date.getFullYear() < 2000) {
        return 'N/A'
      }
      return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return 'N/A'
    }
  }

  const getOrderStatus = (order) => {
    const now = new Date()
    const started = new Date(order.StartedAt || order.StarteddAt || 0)
    const finished = new Date(order.FinishedAt || 0)
    
    if (now < started) {
      return { text: 'Programada', color: 'info', icon: Calendar }
    } else if (now >= started && now <= finished) {
      return { text: 'En Producción', color: 'success', icon: CheckCircle2 }
    } else {
      return { text: 'Finalizada', color: 'secondary', icon: XCircle }
    }
  }

  const getCurrentOrder = (line, ordersFromSAP = []) => {
    // Ya viene en line.activeOrder desde loadLinesWithData
    const currentOrder = line.activeOrder || line.orders?.[0] || null
    
    if (!currentOrder) return null
    
    // PRIORIDAD 3: Si la orden ya no existe en SAP, marcarla como completada y pasar a la siguiente
    // IMPORTANTE: Solo aplicar Prioridad 3 si tenemos órdenes de SAP para comparar
    let orderExistsInSAP = true // Por defecto, asumir que existe si no hay datos de SAP
    
    if (ordersFromSAP.length > 0 && currentOrder) {
      // Normalizar OrderNumber (trim espacios en blanco)
      const currentOrderNum = (currentOrder.OrderNumber || '').trim()
      
      // Si tenemos órdenes de SAP, comparar (normalizando también los de SAP)
      orderExistsInSAP = ordersFromSAP.some(sapOrder => {
        const sapOrderNum = (sapOrder.OrderNumber || sapOrder.AUFNR || '').trim()
        return sapOrderNum === currentOrderNum
      })
      
      // Log para debugging
      if (currentOrderNum === '000010112079') {
        const sapOrderNumbers = ordersFromSAP.map(o => (o.OrderNumber || o.AUFNR || '').trim())
        console.log(`🔍 PRIORIDAD 3 (getCurrentOrder) - Comparando orden ${currentOrderNum}:`, {
          orderNumber: currentOrderNum,
          ordersFromSAP: sapOrderNumbers,
          exists: orderExistsInSAP
        })
        
        if (!orderExistsInSAP) {
          console.log(`🔴 PRIORIDAD 3 - Orden ${currentOrderNum} NO existe en SAP (getCurrentOrder) - buscando siguiente orden`)
        }
      }
    }
    
    // Si la orden no existe en SAP y hay otras órdenes disponibles, buscar la siguiente
    if (ordersFromSAP.length > 0 && !orderExistsInSAP && line.orders && line.orders.length > 1) {
      const nextOrder = line.orders.find(
        order => order.OrderNumber !== currentOrder.OrderNumber
      )
      if (nextOrder) {
        return nextOrder
      }
    }
    
    // Si la orden existe en SAP pero completó su fecha de fin y tiene cantidades pendientes, buscar siguiente activa
    if (orderExistsInSAP) {
      const finishedAt = currentOrder.FinishedAt ? new Date(currentOrder.FinishedAt) : null
      const now = new Date()
      const quantityRemaining = parseFloat(currentOrder.QuantityRemainedToProduce || '0') || 0
      const isCurrentOrderActive = (currentOrder.StartedAt || currentOrder.StarteddAt) && !currentOrder.FinishedAt
      
      // Si completó fecha fin pero aún tiene cantidades pendientes (Prioridad 2), mantenerla pero marcarla como atrasada
      // Si completó fecha fin y no tiene cantidades pendientes, buscar siguiente orden activa
      if (finishedAt && now > finishedAt && quantityRemaining <= 0 && line.orders && line.orders.length > 1) {
        const nextActiveOrder = line.orders.find(
          order => order.OrderNumber !== currentOrder.OrderNumber && 
                   (order.StartedAt || order.StarteddAt) && 
                   !order.FinishedAt
        )
        
        if (!nextActiveOrder) {
          const nextOrder = line.orders.find(
            order => order.OrderNumber !== currentOrder.OrderNumber
          )
          if (nextOrder) {
            return nextOrder
          }
        } else {
          return nextActiveOrder
        }
      }
    }
    
    return currentOrder
  }

  const calculateProgress = (produced, toProduce, order = null, ordersFromSAP = []) => {
    if (!order) {
      // Cálculo básico sin orden
      if (!produced || !toProduce) return 0
      const producedNum = parseFloat(produced) || 0
      const toProduceNum = parseFloat(toProduce) || 0
      if (toProduceNum === 0) return 0
      return Math.min(100, Math.round((producedNum / toProduceNum) * 100))
    }
    
    const now = new Date()
    const finishedAt = order.FinishedAt ? new Date(order.FinishedAt) : null
    const startedAt = order.StartedAt || order.StarteddAt ? new Date(order.StartedAt || order.StarteddAt) : null
    
    // Obtener cantidades desde la API (GAMNG, WEMNG, CANT_RES)
    const quantityToProduce = parseFloat(order.QuantityToProduce || toProduce || '0') || 0
    const quantityProduced = parseFloat(order.QuantityProduced || produced || '0') || 0
    const quantityRemaining = parseFloat(order.QuantityRemainedToProduce || '0') || 0
    
    // PRIORIDAD 3: Si la orden ya no existe en SAP, declararla 100% finalizada
    // IMPORTANTE: Solo aplicar Prioridad 3 si tenemos órdenes de SAP para comparar
    // Si ordersFromSAP está vacío, no podemos determinar si la orden fue removida
    let orderExistsInSAP = true // Por defecto, asumir que existe si no hay datos de SAP
    
    if (ordersFromSAP.length > 0) {
      // Normalizar OrderNumber (trim espacios en blanco)
      const currentOrderNum = (order.OrderNumber || '').trim()
      
      // Si tenemos órdenes de SAP, comparar (normalizando también los de SAP)
      orderExistsInSAP = ordersFromSAP.some(sapOrder => {
        const sapOrderNum = (sapOrder.OrderNumber || sapOrder.AUFNR || '').trim()
        return sapOrderNum === currentOrderNum
      })
      
      // Log detallado para debugging de Prioridad 3
      if (currentOrderNum === '000010112079' || currentOrderNum === '000010125913') {
        const sapOrderNumbers = ordersFromSAP.map(o => (o.OrderNumber || o.AUFNR || '').trim())
        console.log(`🔍 PRIORIDAD 3 - Comparando orden ${currentOrderNum}:`, {
          orderNumber: currentOrderNum,
          ordersFromSAP: sapOrderNumbers,
          exists: orderExistsInSAP,
          foundMatch: ordersFromSAP.find(o => (o.OrderNumber || o.AUFNR || '').trim() === currentOrderNum)
        })
        
        if (!orderExistsInSAP) {
          console.log(`🔴 PRIORIDAD 3 - Orden ${currentOrderNum} NO existe en SAP - marcando como 100% completada`)
        }
      }
    }
    
    if (!orderExistsInSAP) {
      return 100 // Orden completada y removida de SAP
    }
    
    // PRIORIDAD 1: Fechas vigentes con cantidades pendientes de producir
    // Si la fecha fin no ha pasado o no hay fecha fin, calcular progreso normal
    // Una orden está en Prioridad 1 si:
    // - No tiene fecha fin O la fecha fin aún no ha pasado
    // - Y tiene cantidades pendientes
    const isDateValid = !finishedAt || (finishedAt && now <= finishedAt)
    
    if (isDateValid && quantityRemaining > 0) {
      // PRIORIDAD 1: Fechas vigentes con cantidades pendientes - calcular progreso normal desde API
      if (quantityToProduce === 0) return 0
      const progress = Math.min(100, Math.round((quantityProduced / quantityToProduce) * 100))
      console.log(`📊 Prioridad 1 - Orden ${order.OrderNumber}:`, {
        progress,
        quantityProduced,
        quantityToProduce,
        quantityRemaining,
        finishedAt: finishedAt ? finishedAt.toISOString() : 'N/A',
        now: now.toISOString()
      })
      return progress
    }
    
    // PRIORIDAD 2: Fecha fin fuera de plazo pero aún con cantidades restantes (orden atrasada)
    // Una orden está en Prioridad 2 si:
    // - La fecha fin ya pasó
    // - Y aún tiene cantidades pendientes
    if (finishedAt && now > finishedAt && quantityRemaining > 0) {
      // Orden atrasada pero aún con cantidades pendientes - calcular progreso real
      if (quantityToProduce === 0) return 0
      const progress = Math.min(100, Math.round((quantityProduced / quantityToProduce) * 100))
      console.log(`⚠️ Prioridad 2 - Orden ${order.OrderNumber} ATRASADA:`, {
        progress,
        quantityProduced,
        quantityToProduce,
        quantityRemaining,
        finishedAt: finishedAt.toISOString(),
        now: now.toISOString()
      })
      return progress
    }
    
    // Si completó fecha fin y no tiene cantidades pendientes, mostrar 100%
    if (finishedAt && now > finishedAt && quantityRemaining <= 0) {
      return 100
    }
    
    // Cálculo normal de progreso usando cantidades de la API
    if (quantityToProduce === 0) return 0
    return Math.min(100, Math.round((quantityProduced / quantityToProduce) * 100))
  }
  
  const isGlobalView = !selectedFactory
  
  // Cargar estado de InfluxDB para las líneas
  useEffect(() => {
    const loadLinesStatus = async () => {
      if (linesWithData.length === 0 || loadingLineData) return
      
      try {
        const lineCodes = linesWithData.map(line => line.code).filter(code => code)
        if (lineCodes.length === 0) return
        
        // Obtener la fábrica de la primera línea o usar selectedFactory
        const factory = selectedFactory || linesWithData[0]?.factory || ''
        if (!factory) return
        
        console.log('🔄 Cargando estado de InfluxDB para líneas:', { factory, lineCodes })
        
        const response = await api.get(`/api/asset/lines/status?factory=${factory}&lines=${lineCodes.join(',')}`)
        
        // La respuesta puede venir como { data: {...} } o directamente como objeto
        const statusData = response.data || response
        
        // Convertir la respuesta a un objeto más fácil de usar
        const statusMap = {}
        Object.keys(statusData).forEach(lineCode => {
          const statusInfo = statusData[lineCode]
          if (statusInfo && typeof statusInfo === 'object') {
            statusMap[lineCode] = {
              status: statusInfo.status || 'unknown', // 'operativa', 'apagada', 'unknown'
              influx_available: statusInfo.status !== 'unknown' && statusInfo.status !== undefined,
              last_seen: statusInfo.last_seen
            }
          } else {
            statusMap[lineCode] = {
              status: 'unknown',
              influx_available: false
            }
          }
        })
        
        // Para líneas que no están en la respuesta, establecer como desconocidas
        lineCodes.forEach(lineCode => {
          if (!statusMap[lineCode]) {
            statusMap[lineCode] = {
              status: 'unknown',
              influx_available: false
            }
          }
        })
        
        console.log('✅ Estado de InfluxDB cargado:', statusMap)
        setLinesStatus(statusMap)
      } catch (err) {
        console.error('❌ Error al cargar estado de InfluxDB:', err)
        // Si falla, establecer todos como desconocidos
        const unknownStatus = {}
        linesWithData.forEach(line => {
          if (line.code) {
            unknownStatus[line.code] = {
              status: 'unknown',
              influx_available: false
            }
          }
        })
        setLinesStatus(unknownStatus)
      }
    }
    
    loadLinesStatus()
  }, [linesWithData, loadingLineData, selectedFactory])
  
  // Debug: Log del estado de carga
  useEffect(() => {
    console.log('🔍 Estado de carga:', {
      loadingLines,
      loadingLineData,
      linesLength: lines.length,
      linesWithDataLength: linesWithData.length,
      hasError: !!linesError,
      errorMessage: linesError?.message,
      linesStatusCount: Object.keys(linesStatus).length
    })
  }, [loadingLines, loadingLineData, lines.length, linesWithData.length, linesError, linesStatus])

  const isWaitingForTerminalLine =
    isHmiMode && !selectedLine && (loadingLines || loadingLineData || terminalLoading)

  if (isWaitingForTerminalLine) {
    return (
      <div className="min-h-screen bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Cargando línea del terminal…</p>
        </div>
      </div>
    )
  }

  // No bloquear todo el renderizado si hay líneas, solo mostrar loading en la sección específica
  // if (loadingLines && lines.length === 0) {
  //   return (
  //     <div className="min-h-screen bg-gray-900 p-6 flex items-center justify-center">
  //       <div className="text-center">
  //         <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
  //         <p className="text-gray-400">{t.common?.loading || 'Cargando líneas de producción...'}</p>
  //       </div>
  //     </div>
  //   )
  // }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8 text-blue-400" />
              <div>
                <h1 className="text-3xl font-bold text-white">
                  {t.produccion?.ordenesFabricacion?.title || 'Órdenes de Fabricación'}
                </h1>
                <p className="text-gray-400 mt-1">
                  {isGlobalView ? 'Todas las fábricas' : `Fábrica: ${selectedFactory}`}
                </p>
              </div>
            </div>
          </div>

          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="hover:text-white transition-colors flex items-center gap-1"
            >
              <Home className="w-4 h-4" />
              {t.dashboard?.title || 'Dashboard'}
            </button>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">{t.produccion?.ordenesFabricacion?.title || 'Órdenes de Fabricación'}</span>
          </nav>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-900/20 border border-red-500 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{error}</span>
          </div>
        )}

        {/* Indicadores Principales (ocultos en HMI: la línea la fija el terminal) */}
        {!selectedLine && !isHmiMode && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Total de Líneas */}
            <Card>
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <Factory className="w-8 h-8 text-blue-400" />
                  {loadingLines && (
                    <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                  )}
                </div>
                <h3 className="text-2xl font-bold text-white mb-1">
                  {loadingLines ? '...' : linesWithData.length || lines.length}
                </h3>
                <p className="text-sm text-gray-400">Líneas de Producción</p>
              </div>
            </Card>

            {/* Líneas Operativas */}
            <Card>
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <CheckCircle2 className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-1">
                  {linesWithData.filter(line => {
                    const status = linesStatus[line.code]
                    return status?.status === 'operativa'
                  }).length}
                </h3>
                <p className="text-sm text-gray-400">Líneas Operativas</p>
              </div>
            </Card>

            {/* Total de Órdenes */}
            <Card>
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <Package className="w-8 h-8 text-purple-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-1">
                  {linesWithData.reduce((total, line) => total + (line.orders?.length || 0), 0)}
                </h3>
                <p className="text-sm text-gray-400">Total de Órdenes</p>
              </div>
            </Card>

            {/* Líneas con Orden Activa */}
            <Card>
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <Clock className="w-8 h-8 text-orange-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-1">
                  {linesWithData.filter(line => line.activeOrder).length}
                </h3>
                <p className="text-sm text-gray-400">Líneas con Orden Activa</p>
              </div>
            </Card>
          </div>
        )}


        {/* Lines Selection (oculto en HMI) */}
        {!selectedLine && !isHmiMode && (
          <Card className="mb-6">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-white mb-4">
                {t.produccion?.ordenesFabricacion?.selectLine || 'Seleccionar Línea de Producción'}
              </h2>
              {(() => {
                console.log('🔍 Estado de renderizado en UI:', {
                  loadingLines,
                  loadingLineData,
                  linesError: linesError?.message,
                  linesLength: lines.length,
                  linesWithDataLength: linesWithData.length,
                  selectedFactory
                })
                return null
              })()}
              {loadingLines || loadingLineData ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-blue-500 animate-spin mr-2" />
                  <p className="text-gray-400">Cargando líneas de producción...</p>
                </div>
              ) : linesError ? (
                <div className="flex items-center justify-center py-8">
                  <AlertCircle className="w-6 h-6 text-red-500 mr-2" />
                  <p className="text-red-400">Error al cargar líneas: {linesError.message || 'Error desconocido'}</p>
                </div>
              ) : linesWithData.length === 0 ? (
                <div className="text-center py-8">
                  <Factory className="w-12 h-12 text-gray-600 mx-auto mb-2 opacity-50" />
                  <p className="text-gray-400">{t.common?.noData || 'No hay líneas disponibles'}</p>
                  {selectedFactory && (
                    <p className="text-sm text-gray-500 mt-2">Fábrica seleccionada: {selectedFactory}</p>
                  )}
                  {lines.length > 0 && linesWithData.length === 0 && (
                    <p className="text-sm text-yellow-400 mt-2">Líneas encontradas pero sin datos cargados</p>
                  )}
                  <div className="mt-4 text-xs text-gray-600">
                    <p>Debug: loadingLines={String(loadingLines)}, loadingLineData={String(loadingLineData)}</p>
                    <p>lines.length={lines.length}, linesWithData.length={linesWithData.length}</p>
                    {linesError && <p className="text-red-400">Error: {linesError.message}</p>}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {linesWithData.map((line, index) => {
                    // Usar las órdenes actuales como referencia de SAP (vienen de la última sincronización)
                    const ordersFromSAP = line.orders || []
                    const currentOrder = getCurrentOrder(line, ordersFromSAP)
                    const orderStatus = currentOrder ? getOrderStatus(currentOrder) : null
                    const orderProgress = currentOrder ? calculateProgress(
                      currentOrder.QuantityProduced, 
                      currentOrder.QuantityToProduce,
                      currentOrder,
                      ordersFromSAP
                    ) : 0
                    
                    // Detectar si la orden está atrasada (Prioridad 2)
                    const isOrderDelayed = currentOrder && (() => {
                      const finishedAt = currentOrder.FinishedAt ? new Date(currentOrder.FinishedAt) : null
                      const now = new Date()
                      const quantityRemaining = parseFloat(currentOrder.QuantityRemainedToProduce || '0') || 0
                      return finishedAt && now > finishedAt && quantityRemaining > 0
                    })()
                    
                    // Detectar si la orden ya no existe en SAP (Prioridad 3)
                    const isCompletedAndRemoved = currentOrder && ordersFromSAP.length > 0 && 
                      !ordersFromSAP.some(sapOrder => sapOrder.OrderNumber === currentOrder.OrderNumber)
                    
                    // Obtener estado de InfluxDB
                    const lineStatus = linesStatus[line.code] || {
                      status: 'unknown',
                      influx_available: line.influx_available || false
                    }
                    
                    // Determinar el estado operativo
                    const isOperativo = lineStatus.status === 'operativa'
                    const isApagada = lineStatus.status === 'apagada'
                    const statusText = isOperativo ? 'Operativo' : isApagada ? 'Apagada' : 'Sin datos'
                    const statusVariant = isOperativo ? 'success' : isApagada ? 'secondary' : 'secondary'
                    
                    // Asegurar que haya un key único
                    const lineKey = line.id || line.code || `line-${index}`
                    
                    return (
                      <button
                        key={lineKey}
                        onClick={() => {
                          console.log('🖱️ Línea seleccionada:', {
                            id: line.id,
                            code: line.code,
                            factory: line.factory,
                            sap_code: line.sap_code,
                            location: line.location
                          })
                          setSelectedLine(line)
                          setSelectedOrder(null)
                        }}
                        className="p-4 bg-gray-800 hover:bg-gray-700 rounded-lg border border-gray-700 hover:border-blue-500 transition-all text-left"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Factory className={`w-5 h-5 ${isOperativo ? 'text-green-400' : isApagada ? 'text-gray-500' : 'text-blue-400'}`} />
                          <Badge variant={statusVariant}>
                            {statusText}
                          </Badge>
                        </div>
                        <h3 className="font-semibold text-white mb-1">{line.line}</h3>
                        <p className="text-sm text-gray-400 mb-3">{line.location || line.factory}</p>
                        
                        {currentOrder ? (
                          <div className="mt-3 pt-3 border-t border-gray-700">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-gray-400">Orden de Fabricación</span>
                              {orderStatus && (
                                <Badge variant={orderStatus.color} className="text-xs">
                                  {orderStatus.text}
                                </Badge>
                              )}
                            </div>
                            <div className="space-y-1.5">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400">OF:</span>
                                <span className="text-sm font-semibold text-white">{currentOrder.OrderNumber}</span>
                              </div>
                              {currentOrder.ProductName && (
                                <p className="text-xs font-medium text-white">
                                  {currentOrder.ProductName}
                                </p>
                              )}
                              {currentOrder.ProductDescription && (
                                <p className="text-xs text-gray-300 line-clamp-2">
                                  {currentOrder.ProductDescription}
                                </p>
                              )}
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-gray-400">
                                  Producido: <span className="text-white font-medium">
                                    {currentOrder.QuantityProduced || '0'}
                                  </span> / <span className="text-gray-400">
                                    {currentOrder.QuantityToProduce || '0'}
                                  </span> {currentOrder.MeasurementUnit || ''}
                                </span>
                              </div>
                              {currentOrder.QuantityRemainedToProduce && (
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-orange-400 font-medium">
                                    Pendiente: {currentOrder.QuantityRemainedToProduce} {currentOrder.MeasurementUnit || ''}
                                  </span>
                                </div>
                              )}
                              {orderProgress > 0 && (
                                <div className="w-full bg-gray-700 rounded-full h-1.5 mt-1">
                                  <div
                                    className="h-full bg-blue-600 rounded-full transition-all"
                                    style={{ width: `${orderProgress}%` }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="mt-3 pt-3 border-t border-gray-700">
                            <p className="text-xs text-gray-500">Sin orden de fabricación</p>
                          </div>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Orders Display */}
        {selectedLine && (
          <>
            {!selectedOrder && (
            <Card className="mb-6">
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {!isHmiMode && (
                    <button
                      onClick={() => {
                        setSelectedLine(null)
                        setSelectedOrder(null)
                      }}
                      className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <ChevronRight className="w-5 h-5 text-gray-400 rotate-180" />
                    </button>
                    )}
                    <div>
                      <h2 className="text-2xl font-bold text-white">{selectedLine.line || selectedLine.code}</h2>
                      <p className="text-gray-400">
                        Órdenes enviadas a SAP · {selectedLine.location || selectedLine.factory}
                        {isHmiMode && terminal?.name ? ` · ${terminal.name}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <button
                      onClick={handleRefreshSAP}
                      disabled={loadingSentOrders || isSyncing}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg transition-colors"
                    >
                      {(loadingSentOrders || isSyncing) ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      <span className="text-white">Actualizar desde SAP</span>
                    </button>
                    {lastSyncDate && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-400">
                        <Clock className="w-3 h-3" />
                        <span>
                          Última sincronización: {lastSyncDate.toLocaleString('es-ES', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
            )}

            {selectedOrder ? (
              <ManufacturingOrderDetailPanel
                selectedOrder={selectedOrder}
                selectedLine={selectedLine}
                onBack={() => {
                  setSelectedOrder(null)
                  setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100)
                }}
              />
            ) : loadingSentOrders ? (
              <Card>
                <div className="p-12 text-center">
                  <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mx-auto mb-4" />
                  <p className="text-gray-400">Cargando órdenes enviadas a SAP...</p>
                  {selectedLine && (
                    <p className="text-gray-500 text-sm mt-2">
                      Línea: {selectedLine.code} | Fábrica: {selectedLine.factory || 'N/A'}
                    </p>
                  )}
                </div>
              </Card>
            ) : sentOrdersError ? (
              <Card>
                <div className="p-12 text-center">
                  <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                  <p className="text-red-400 text-lg">Error al cargar órdenes enviadas a SAP</p>
                  <p className="text-gray-500 text-sm mt-2">
                    {sentOrdersError.message || 'Error desconocido'}
                  </p>
                  <button
                    onClick={() => refetchSentOrders()}
                    className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    Reintentar
                  </button>
                </div>
              </Card>
            ) : sentToSAPOrders.length === 0 ? (
              <Card>
                <div className="p-12 text-center">
                  <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">No hay órdenes enviadas a SAP en esta línea</p>
                  {selectedLine && (
                    <div className="mt-4 text-sm text-gray-500">
                      <p>Línea: {selectedLine.code}</p>
                      <p>Fábrica: {selectedLine.factory || 'N/A'}</p>
                    </div>
                  )}
                  <p className="text-gray-500 text-sm mt-4">
                    Las órdenes aparecerán aquí cuando todos sus períodos de consumo hayan sido enviados a SAP
                  </p>
                </div>
              </Card>
            ) : (
              <Card className="mb-6">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-indigo-400" />
                        Órdenes Enviadas a SAP
                      </h3>
                      <p className="text-sm text-gray-400 mt-1">
                        Pulsa sobre una orden para ver el detalle completo
                      </p>
                    </div>
                    <Badge variant="secondary">
                      {sentToSAPOrders.length} {sentToSAPOrders.length === 1 ? 'orden' : 'órdenes'}
                    </Badge>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Orden</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Producto</th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-400">Progreso</th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-400">Producido</th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-400">Objetivo</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Inicio</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Fin</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Enviada a SAP</th>
                          <th className="w-10" />
                        </tr>
                      </thead>
                      <tbody>
                        {[...sentToSAPOrders]
                          .sort((a, b) => {
                            const dateA = a.SentToSAPAt ? new Date(a.SentToSAPAt) : new Date(0)
                            const dateB = b.SentToSAPAt ? new Date(b.SentToSAPAt) : new Date(0)
                            return dateB - dateA
                          })
                          .map((order, index) => {
                            const progress = calculateProgress(
                              order.QuantityProduced,
                              order.QuantityToProduce,
                              order,
                              sentToSAPOrders
                            )

                            return (
                              <tr
                                key={order.OrderNumber || index}
                                onClick={() => {
                                  setSelectedOrder(order)
                                  setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100)
                                }}
                                className="border-b border-gray-800 hover:bg-indigo-900/30 transition-colors bg-indigo-900/10 cursor-pointer group"
                              >
                                <td className="py-3 px-4">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-white group-hover:text-indigo-200">{order.OrderNumber}</span>
                                    <Badge variant="info" className="bg-indigo-600 text-xs">
                                      Enviada a SAP
                                    </Badge>
                                    {order.OrderNType && (
                                      <Badge variant="secondary" className="text-xs">
                                        {order.OrderNType}
                                      </Badge>
                                    )}
                                  </div>
                                </td>
                                <td className="py-3 px-4">
                                  <div>
                                    <p className="text-white text-sm font-medium">{order.ProductName || 'N/A'}</p>
                                    {order.ProductDescription && (
                                      <p className="text-gray-400 text-xs line-clamp-1">{order.ProductDescription}</p>
                                    )}
                                  </div>
                                </td>
                                <td className="py-3 px-4 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <span className="text-white text-sm font-medium">{progress}%</span>
                                    <div className="w-16 bg-gray-700 rounded-full h-2 overflow-hidden">
                                      <div
                                        className="h-full bg-indigo-600 transition-all"
                                        style={{ width: `${progress}%` }}
                                      />
                                    </div>
                                  </div>
                                </td>
                                <td className="py-3 px-4 text-right">
                                  <span className="text-white text-sm">
                                    {order.QuantityProduced || '0'} {order.MeasurementUnit || ''}
                                  </span>
                                </td>
                                <td className="py-3 px-4 text-right">
                                  <span className="text-gray-400 text-sm">
                                    {order.QuantityToProduce || '0'} {order.MeasurementUnit || ''}
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="text-gray-400 text-sm">{formatDate(order.StartedAt || order.StarteddAt)}</span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="text-gray-400 text-sm">{formatDate(order.FinishedAt)}</span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="text-indigo-300 text-sm font-medium">
                                    {formatDate(order.SentToSAPAt)}
                                  </span>
                                </td>
                                <td className="py-3 px-2">
                                  <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-indigo-300 transition-colors" />
                                </td>
                              </tr>
                            )
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default OrdenesFabricacion

